"use client";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import useRedirect from "@/hooks/useRedirect";
import { mixpanelEvent } from "@/lib/mixpanels/mixpanel";
import { useAuthStore } from "@/lib/auth/AuthStore";
import { Icon } from "@bookips/sds";
import { useRouter } from "next/router";
import { REGISTRATION_PAGE, VIEWER_PAGE } from "src/settings/constant";
import { useDialogStore } from "src/store/useDialogStore";
import useClaimFreeTrial from "../../hooks/useClaimFreeTrial";
const FREE_TRIAL_DIALOG_ID = "free-trial-dialog";
/**
 * 무료체험 다이얼로그 훅
 * - useDialogStore를 사용하여 다이얼로그 관리
 * - 별도의 컴포넌트 렌더링 없이 훅만으로 다이얼로그 표시
 */ export const useFreeTrialDialog = ()=>{
    const router = useRouter();
    const { isLoggedIn  } = useAuthStore();
    const { open , close  } = useDialogStore();
    const { mutateAsync: claimFreeTrial  } = useClaimFreeTrial();
    const { redirectToLogin , redirectTo  } = useRedirect();
    // 로그인 유도 다이얼로그
    const openLoginDialog = ()=>{
        open({
            id: FREE_TRIAL_DIALOG_ID,
            type: "confirm",
            props: {
                title: "무료 자료를 받기 위해서는 로그인이 필요해요.",
                confirmText: "회원가입",
                cancelText: "로그인",
                onConfirm: ()=>{
                    redirectTo(REGISTRATION_PAGE);
                },
                onCancel: ()=>{
                    redirectToLogin();
                },
                contentProps: {
                    onInteractOutside: ()=>{
                        close(FREE_TRIAL_DIALOG_ID);
                    }
                }
            }
        });
    };
    // 무료로 받기 확인 다이얼로그
    const openConfirmDialog = (productId, promotionId)=>{
        const confirmPopupTitle = "이 자료를 무료로 받을까요?";
        mixpanelEvent("Free Promotion Popup Viewed", {
            "Popup Type": "무료 자료 획득 유도",
            "Popup Title": confirmPopupTitle
        });
        open({
            id: FREE_TRIAL_DIALOG_ID,
            type: "confirm",
            props: {
                title: confirmPopupTitle,
                confirmText: "무료로 받기",
                cancelText: "취소",
                onConfirm: async ()=>{
                    try {
                        const response = await claimFreeTrial({
                            productId,
                            promotionId
                        });
                        if (response.success) {
                            if (!response.license.id) {
                                return;
                            }
                            const viewerUrl = "".concat(VIEWER_PAGE, "/").concat(response.license.id);
                            openSuccessDialog(viewerUrl);
                        }
                    } catch (error) {
                        ;
                    }
                },
                contentProps: {
                    onInteractOutside: ()=>{
                        close(FREE_TRIAL_DIALOG_ID);
                    }
                }
            }
        });
    };
    // 성공 다이얼로그
    const openSuccessDialog = (viewerUrl)=>{
        const successPopupTitle = "무료 체험 자료를 받았어요! 바로 열람 할까요?";
        mixpanelEvent("Get Free Handout Completed", {
            "Popup Type": "무료 자료 획득 완료",
            "Popup Title": successPopupTitle
        });
        open({
            id: FREE_TRIAL_DIALOG_ID,
            type: "confirm",
            props: {
                icon: /*#__PURE__*/ _jsx("div", {
                    className: "py-0.5",
                    children: /*#__PURE__*/ _jsx(Icon.Success_fill, {
                        className: "text-status-data size-6"
                    })
                }),
                title: /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        "무료 체험 자료를 받았어요!",
                        /*#__PURE__*/ _jsx("br", {}),
                        "바로 열람 할까요?"
                    ]
                }),
                confirmText: "자료 열람하기",
                cancelText: "닫기",
                onConfirm: ()=>{
                    router.push(viewerUrl);
                },
                classes: {
                    dialogTitle: {
                        titleContainer: "md:items-start"
                    }
                },
                contentProps: {
                    onInteractOutside: ()=>{
                        close(FREE_TRIAL_DIALOG_ID);
                    }
                }
            }
        });
    };
    // 무료로 받기 버튼 클릭 시 호출
    const openFreeTrialDialog = (productId, promotionId)=>{
        if (isLoggedIn) {
            openConfirmDialog(productId, promotionId);
        } else {
            openLoginDialog();
        }
    };
    // 다이얼로그 닫기
    const closeDialog = ()=>{
        close(FREE_TRIAL_DIALOG_ID);
    };
    return {
        openFreeTrialDialog,
        closeDialog
    };
};
